/****************************************************************************
 * COPYRIGHT (c) 2024 Harman Connected Services
 *
 * All rights reserved
 *
 * This software embodies materials and concepts which are
 * confidential to Ignite Product and is made available solely
 * pursuant to the terms of a written license agreement with
 * Ignite Product.
 *
 * Designed and Developed by Harman International Industries, Inc.
 ***************************************************************************/

import React, { useEffect, useState, useRef } from "react";
import PropTypes from "prop-types";

import {
  Box,
  Button,
  Typography,
  Tooltip,
  IconButton,
  Backdrop,
  CircularProgress,
  Modal,
  Chip,
  Divider,
} from "@mui/material";

import { useSelector, useDispatch } from "react-redux";

import "./OemAdminApplicationDetailsTargeting.css";

import OemTargetPopup from "../../page/oem-admin/targetPopup/oem-target-popup";
import OemTargetECUTypePopup from "../../page/oem-admin/targetPopup/oem-targetECU-type-popup";
import { fetchOemAdminTargetEcuDetails } from "../../Services/redux/Target-Ecu-Types/OemAdminTargetEcuActions";
import OemAdminTargetingMmyPopUp from "../OEM-Admin-Components/OemAdminTargetingMmyPopUp/OemAdminTargetingMmyPopUp";
import { checkEuropeanCountries, checkTagCountries, updateTargetingTags } from "../../Services/redux/DSAOemAdminUrl/DSAOemAdminActions";
import { useParams } from "react-router-dom";
import APIManager from "../../Services/ApiManager/ApiManager";
import ClearIcon from "@mui/icons-material/Clear";
import { getConfig } from "../../config";

function OemAdminApplicationDetailsTargeting({ targettingData, appId }) {
  const dispatch = useDispatch();
  const [openSeeMoreCountries, setOpenSeeMoreCountries] = useState(false);
  const [openPopup, setOpenPopup] = useState(false);
  const [OpenEcuPopup, setOpenEcuPopup] = useState(false);
  // Track API call for ECU types per appId
  const [ecuApiCalled, setEcuApiCalled] = useState(false);

  const [mmyInclusion, setMmyInclusion] = useState([]);
  const [mmyExclusion, setMmyExclusion] = useState([]);
  const [mmyOemAdmin, setMmyOemAdmin] = useState([]);
  const [openMmyPopUp, setOpenMmyPopUp] = useState(false);
  const [ecuExclusions, setEcuExclusions] = useState([]);

  // Add safety check to prevent blank screen
  const [isSaving, setIsSaving] = useState(false);
  const [loader, setLoader] = useState(false);
  const [selectedCountries, setSelectedCountries] = useState([]);

  // All hooks must be declared before any early returns
  const params = useParams();
  const ecuTypeData = useSelector((state) => state.targetEcuType.ecu);
  const submissionData = useSelector(
    (state) => state.submissionDetails.submissionDetails
  );

  // Ref to track the latest accumulated tags across saves
  // This prevents each save from starting with stale targettingData.tags
  const latestTagsRef = useRef(null);
  const hasLocalSavesRef = useRef(false);

  // All useEffect hooks must be before any early returns
  useEffect(() => {
    if (targettingData?.tags) {
      // Only reset from prop when no local saves have been made
      if (!hasLocalSavesRef.current) {
        latestTagsRef.current = targettingData.tags;
      }

      const currentCountries =
        targettingData.tags.find((tag) => tag.key === "country")?.values || [];
      if (!hasLocalSavesRef.current) {
        setSelectedCountries(currentCountries);
      }

      // Initialize ECU exclusions state
      const currentEcuExclusions =
        targettingData.tags.find((tag) => tag.key === "ecuType_exclusion")
          ?.values || [];
      if (!hasLocalSavesRef.current) {
        setEcuExclusions(currentEcuExclusions);
      }
    }
  }, [targettingData]);



  useEffect(() => {
    const tags = submissionData?.tags;
    if (!hasLocalSavesRef.current) {
      let mmyInclusionOemAdmin = tags?.filter(
        (valueOfTag) => valueOfTag.key === "mmy_inclusion"
      );

      setMmyInclusion(mmyInclusionOemAdmin?.[0]?.values || []);
      let mmyExclusionOemAdmin = tags?.filter(
        (valueOfTag) => valueOfTag.key === "mmy_exclusion"
      );

      setMmyExclusion(mmyExclusionOemAdmin?.[0]?.values || []);
    }
  }, [submissionData]);

  // Add useEffect to log MMY state changes for debugging
  useEffect(() => {}, [mmyInclusion, mmyExclusion, mmyOemAdmin]);

  if (!targettingData) {
    console.warn(
      "OemAdminApplicationDetailsTargeting: Missing targettingData prop"
    );
    return (
      <div
        id="oem-admin-targeting"
        className="oem-admin-targetting-main-section"
      >
        <Typography sx={{ fontSize: "20px", fontWeight: 600 }}>
          Loading Targeting Information...
        </Typography>
      </div>
    );
  }
  const handleCloseMmyPopUp = () => {
    setOpenMmyPopUp(false);
  };
  // ...existing code...
  const modalStyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    bgcolor: "background.paper",
    width: "585px",
    height: "412px",
    borderRadius: "8px",
    borderWidth: "1px",
    gap: "24px",
  };

  const handleOemAdminMmy = async () => {
    setLoader(true);

    try {
      const response = await APIManager.getOemAdminMmy(params.appId);
      setMmyOemAdmin(response);
      setLoader(false);
      // Open popup only after data is loaded
      setOpenMmyPopUp(true);
    } catch (error) {
      console.error("Error fetching MMY data:", error);
      setLoader(false);
    }
  };

  const allCountries =
    targettingData?.tags.find((tag) => tag.key === "content_country")?.values ||
    [];
  const cpuType =
    targettingData?.tags.find((tag) => tag.key === "cpuType")?.values || [];
  // Use local state for ECU exclusions to ensure real-time updates
  const ecuType_exclusion = ecuExclusions;
  const headUnitType =
    targettingData?.tags.find((tag) => tag.key === "headUnitType")?.values ||
    [];

  // Update selectedCountries when targettingData changes
  const reviewStatus = targettingData?.reviewStatus;

  // Calculate display status based on actual selections vs total available
  const getMMYDisplayStatus = () => {
    // If we have ALL_ALL_ALL inclusion and no exclusions, everything is selected
    if (mmyInclusion.includes("ALL_ALL_ALL") && mmyExclusion.length === 0) {
      return "All available";
    }

    // If we have exclusions, show exclusion count
    if (mmyExclusion.length > 0) {
      return `Excluded (${mmyExclusion.length})`;
    }

    // If no MMY data available yet, show loading or default
    if (!mmyOemAdmin || mmyOemAdmin.length === 0) {
      return "All available";
    }

    // Calculate total available MMY combinations
    let totalAvailable = 0;
    mmyOemAdmin.forEach((make) => {
      make.models?.forEach((model) => {
        totalAvailable += model.years?.length || 0;
      });
    });

    // For specific inclusions (not ALL_ALL_ALL), calculate what's excluded
    if (!mmyInclusion.includes("ALL_ALL_ALL")) {
      const selectedCount = mmyInclusion.length;
      const excludedCount = totalAvailable - selectedCount;

      if (excludedCount <= 0) {
        return "All available";
      } else {
        return `Excluded (${excludedCount})`;
      }
    }

    // Default fallback
    return "All available";
  };

  const makes = ecuTypeData?.map((item) => item.make) || [];
  const ecuTypes = ecuTypeData?.flatMap((item) => item.ecuTypes) || [];

  const handleSave = async (keyOrUpdates, values, reasonForExclusion) => {
    // Prevent multiple save operations
    if (isSaving) {
      return;
    }
    // Safety check for required data
    if (!targettingData?.tags) {
      return;
    }

    // Support both single key/values and array of {key, values} pairs
    const updates = Array.isArray(keyOrUpdates)
      ? keyOrUpdates
      : [{ key: keyOrUpdates, values }];

    // Use accumulated tags ref so each save builds on previous saves
    const baseTags = latestTagsRef.current || targettingData.tags;
    let updatedTags = [...baseTags];
    updates.forEach(({ key, values: vals }) => {
      updatedTags = updatedTags.map((tag) =>
        tag.key === key ? { ...tag, values: vals } : tag
      );
    });

    // Update local state immediately for UI responsiveness
    updates.forEach(({ key, values: vals }) => {
      if (key === "country") {
        const euCountriesAdded = vals.some((code) =>
          getConfig().CHECK_EUROPEAN_COUNTRIES.includes(code)
        );
        setSelectedCountries(vals);
        dispatch(checkEuropeanCountries(euCountriesAdded));
        dispatch(checkTagCountries(vals));
      }
      if (key === "ecuType_exclusion") {
        setEcuExclusions(vals);
      }
      if (key === "mmy_inclusion") {
        setMmyInclusion(vals);
      }
      if (key === "mmy_exclusion") {
        setMmyExclusion(vals);
      }
    });

    setLoader(true);
    setIsSaving(true);

    try {
      const response = await APIManager.updatePortfolioContentDetails(
        sessionStorage.getItem("portfolioId"),
        appId,
        updatedTags,
        reasonForExclusion
      );

      // Sync updated tags to Redux so ApproveApplicationPopUp uses fresh data
      dispatch(updateTargetingTags(updatedTags));

      // Update the ref so next save builds on this save's result
      latestTagsRef.current = updatedTags;
      hasLocalSavesRef.current = true;

      setOpenPopup(false);
      setOpenEcuPopup(false);
      setOpenMmyPopUp(false);

      return response;
    } catch (err) {
      console.error("OEM Admin - Save operation failed:", err);

      // Revert local state on error
      updates.forEach(({ key }) => {
        if (key === "country") {
          const originalCountries =
            targettingData?.tags.find((tag) => tag.key === "country")?.values ||
            [];
          setSelectedCountries(originalCountries);
        }
        if (key === "ecuType_exclusion") {
          const originalEcuExclusions =
            targettingData?.tags.find((tag) => tag.key === "ecuType_exclusion")
              ?.values || [];
          setEcuExclusions(originalEcuExclusions);
        }
      });

      // Show error to user
      throw err;
    } finally {
      setLoader(false);
      setIsSaving(false);
    }
  };

  // Handler for ECU popup refresh
  const handleEcuRefresh = (excludedCount) => {};

  const countryList = selectedCountries;

  const sortedCountryList = [...countryList].sort((a, b) => a.localeCompare(b));
  const showCountryList =
    sortedCountryList.length > 13
      ? sortedCountryList.slice(0, 13)
      : sortedCountryList;

  return (
    <>
      {(loader || isSaving) && (
        <Backdrop
          sx={(theme) => ({ color: "#fff", zIndex: theme.zIndex.drawer + 1 })}
          open={true}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      )}

      <div
        id="oem-admin-targeting"
        className="oem-admin-targetting-main-section"
        style={{
          opacity: loader || isSaving ? 0.6 : 1,
          pointerEvents: loader || isSaving ? "none" : "auto",
        }}
      >
        <Typography sx={{ fontSize: "20px", fontWeight: 600 }}>
          Targeting
        </Typography>

        <div className="oem-admin-targetting-content">
          <div className="oem_targeted_countries">
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "8px 15px 5px 15px",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Tooltip title={sortedCountryList.join(",")}>
                  <Typography
                    sx={{
                      fontSize: "14px",
                      fontWeight: 600,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      maxWidth: "200px",
                    }}
                  >
                    Countries <span style={{ color: "#B50B00" }}>*</span>{" "}
                  </Typography>
                </Tooltip>
              </Box>

              <Box>
                <Button
                  className="set-button"
                  variant="outlined"
                  onClick={() => {
                    setOpenPopup(true);
                  }}
                  style={{ textTransform: "none" }}
                  sx={{
                    "&.Mui-disabled": {
                      border: "1px solid #1976d2",
                      color: "#1976d2",
                      opacity: 0.5,
                    },
                  }}
                  disabled={
                    sessionStorage.getItem("readonly_OemAdmin") === "true"
                  }
                >
                  {reviewStatus.includes("FORCE_UNINSTALL") ||
                  reviewStatus.includes("DELIST")
                    ? "View"
                    : "Set"}
                </Button>
              </Box>
            </Box>
            <div className="oem_targeted_countries_chip">
              {showCountryList.map((country) => (
                <Chip
                  key={country}
                  label={country}
                  sx={{
                    marginLeft: "4px",
                    width: "max-content",
                    borderRadius: "4px",
                    backgroundColor: "#F3F3F3",
                  }}
                  size="small"
                />
              ))}

              {sortedCountryList.length > 13 && (
                <Typography
                  sx={{
                    cursor: "pointer",
                    color: "rgb(21, 101, 192)",
                    fontSize: "12px",
                  }}
                  onClick={() => setOpenSeeMoreCountries(true)}
                >
                  See More
                </Typography>
              )}
            </div>
          </div>
          <div className="oem_targeted_head-unit">
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "8px 15px 0px 15px",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Typography
                  sx={{
                    fontSize: "14px",
                    fontWeight: "600",
                  }}
                >
                  Head Unit Models <span style={{ color: "#B50B00" }}>*</span>
                </Typography>
              </Box>
            </Box>
            <Typography
              sx={{
                paddingLeft: "15px",
                fontSize: "12px",
                fontWeight: "400",
                color: "#5A5B5C",
                paddingBottom: "10px",
              }}
            >
              {" "}
              {headUnitType?.trim ? headUnitType : "All available"}
            </Typography>
          </div>
          <div className="oem_targeted_make-model">
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "8px 15px 0px 15px",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Typography
                  sx={{
                    fontSize: "14px",
                    fontWeight: "600",
                  }}
                >
                  Make/Model/Year <span style={{ color: "#B50B00" }}>*</span>
                </Typography>
              </Box>

              <Box>
                <Button
                  className="set-button"
                  variant="outlined"
                  onClick={handleOemAdminMmy}
                  style={{ textTransform: "none" }}
                  sx={{
                    "&.Mui-disabled": {
                      border: "1px solid #1976d2",
                      color: "#1976d2",
                      opacity: 0.5,
                    },
                  }}
                  disabled={
                    sessionStorage.getItem("readonly_OemAdmin") === "true"
                  }
                >
                  {reviewStatus.includes("FORCE_UNINSTALL") ||
                  reviewStatus.includes("DELIST")
                    ? "View"
                    : "Set"}
                </Button>
              </Box>
            </Box>

            <Typography
              sx={{
                paddingLeft: "15px",
                fontSize: "12px",
                fontWeight: "400",
                color: "#5A5B5C",
                paddingBottom: "10px",
              }}
            >
              {(() => {
                const status = getMMYDisplayStatus();
                return status;
              })()}
            </Typography>
            {process.env.NODE_ENV === "development" && (
              <Typography
                sx={{ paddingLeft: "15px", fontSize: "10px", color: "red" }}
              ></Typography>
            )}
          </div>
          <div className="oem_targeted_cpu-type">
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "8px 15px 0px 15px",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Typography
                  sx={{
                    fontSize: "14px",
                    fontWeight: "600",
                  }}
                >
                  CPU Type <span style={{ color: "#B50B00" }}>*</span>
                </Typography>
              </Box>
              <Box></Box>
            </Box>

            <Typography
              sx={{
                paddingLeft: "15px",
                fontSize: "12px",
                fontWeight: "400",
                color: "#5A5B5C",
                paddingBottom: "10px",
              }}
            >
              {" "}
              {cpuType}
            </Typography>
          </div>
          <div className="oem_targeted_ecu-type">
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "8px 15px 0px 15px",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Typography
                  sx={{
                    fontSize: "14px",
                    fontWeight: "600",
                  }}
                >
                  ECU Type
                </Typography>
              </Box>

              <Box>
                <Button
                  className="set-button"
                  variant="outlined"
                  onClick={async () => {
                    if (appId && !ecuApiCalled[appId]) {
                      try {
                        setLoader(true);
                        dispatch(
                          fetchOemAdminTargetEcuDetails(
                            sessionStorage.getItem("portfolioId"),
                            appId
                          )
                        );
                        setEcuApiCalled(true);
                      } catch (error) {
                        console.error("Error fetching ECU details:", error);
                        setEcuApiCalled(false);
                      } finally {
                        setLoader(false);
                      }
                    }
                    setOpenEcuPopup(true);
                  }}
                  style={{ textTransform: "none" }}
                  sx={{
                    "&.Mui-disabled": {
                      border: "1px solid #1976d2",
                      color: "#1976d2",
                      opacity: 0.5,
                    },
                  }}
                  disabled={
                    sessionStorage.getItem("readonly_OemAdmin") === "true"
                  }
                >
                  {reviewStatus.includes("FORCE_UNINSTALL") ||
                  reviewStatus.includes("DELIST")
                    ? "View"
                    : "Set"}
                </Button>
              </Box>
            </Box>
            {ecuType_exclusion && ecuType_exclusion.length > 0 ? (
              <Typography
                sx={{
                  paddingLeft: "15px",
                  fontSize: "12px",
                  fontWeight: "400",
                  color: "#5A5B5C",
                  paddingBottom: "10px",
                }}
              >
                Excluded ({ecuType_exclusion.length})
              </Typography>
            ) : (
              <Typography
                sx={{
                  paddingLeft: "15px",
                  fontSize: "12px",
                  fontWeight: "400",
                  color: "#5A5B5C",
                  paddingBottom: "10px",
                }}
              >
                All available
              </Typography>
            )}
          </div>
        </div>
      </div>
      <Modal
        open={openSeeMoreCountries}
        onClose={() => setOpenSeeMoreCountries(false)}
      >
        <Box
          className="see-more-countries-popup"
          sx={{
            width: "444px",
            maxHeight: "435px",
            backgroundColor: "white",
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "background.paper",
            borderRadius: "4px",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <div className="see-more-countries-popup-content">
            <div className="see-more-countries-popup-header">
              <Typography
                sx={{
                  fontWeight: "500",
                  fontSize: "20px",
                  marginLeft: "7px",
                }}
              >
                Countries
              </Typography>
              <IconButton onClick={() => setOpenSeeMoreCountries(false)}>
                <ClearIcon />
              </IconButton>
            </div>
            <Divider
              sx={{
                marginTop: "10px !important",
                marginLeft: "-16px",
                width: "444px",
                display: "flex",
                flexWrap: "nowrap",
              }}
            />
            <div
              className="see-more-countries-popup-body"
              style={{
                maxHeight: "300px",
                overflowY: "auto",
                padding: "16px",
              }}
            >
              {sortedCountryList.map((country) => (
                <Chip
                  key={country}
                  label={country}
                  sx={{
                    marginLeft: "8px",
                    marginBottom: "8px",
                    width: "max-content",
                    borderRadius: "4px",
                    backgroundColor: "#F3F3F3",
                  }}
                  size="small"
                />
              ))}
            </div>
          </div>

          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: "12px",
              padding: "16px",
              borderTop: "1px solid #E0E0E0",
              backgroundColor: "#FAFAFA",
              borderRadius: "0 0 4px 4px",
            }}
          >
            <Button
              variant="text"
              onClick={() => setOpenSeeMoreCountries(false)}
              sx={{
                backgroundColor: "#1214151A",
                color: "#666",
                textTransform: "none",
                fontWeight: 500,
                padding: "6px 24px",
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={() => setOpenSeeMoreCountries(false)}
              sx={{
                backgroundColor: "#1976D2",
                color: "white",
                textTransform: "none",
                fontWeight: 500,
                padding: "6px 24px",
              }}
            >
              Ok
            </Button>
          </Box>
        </Box>
      </Modal>

      <OemTargetPopup
        open={openPopup}
        onClose={() => setOpenPopup(false)}
        allCountries={allCountries}
        selectedCountries={selectedCountries}
        setSelectedCountries={setSelectedCountries}
        handleSave={handleSave}
        reviewStatus={reviewStatus}
      />

      <Modal
        open={openMmyPopUp}
        onClose={handleCloseMmyPopUp}
        aria-labelledby="mmy-popup-modal"
      >
        <Box sx={modalStyle}>
          <OemAdminTargetingMmyPopUp
            mmyData={mmyOemAdmin}
            mmyInclusion={mmyInclusion}
            mmyExclusion={mmyExclusion}
            onClose={handleCloseMmyPopUp}
            mmySave={handleSave}
            reviewStatus={reviewStatus}
            targetingData={targettingData}
          />
        </Box>
      </Modal>

      <OemTargetECUTypePopup
        open={OpenEcuPopup}
        onClose={() => setOpenEcuPopup(false)}
        ecuTypeObjList={ecuTypeData}
        makes={makes}
        ecuTypes={ecuTypes}
        exclusions={ecuType_exclusion}
        onSave={handleSave}
        onRefresh={handleEcuRefresh}
        reviewStatus={reviewStatus}
      />
    </>
  );
}

OemAdminApplicationDetailsTargeting.propTypes = {
  targettingData: PropTypes.object,
  appId: PropTypes.string,
};

export default OemAdminApplicationDetailsTargeting;
